import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME, buildPageTitle } from '../utils/pageTitle.js';
import {
  webApplicationSchema,
  collectionPageSchema,
  webPageSchema,
  breadcrumbSchema,
} from '../utils/schema.js';

// ─────────────────────────────────────────────────────────────────────────
// <Seo> — per-route document head
//
// Why this exists: index.html ships ONE set of <title>/description/canonical
// for the whole SPA. Every route therefore inherited the homepage's title and,
// worse, the homepage's canonical (https://windowwashingguide.com/). To a
// crawler that made every page look like a near-duplicate pointing away from
// itself — the "Alternate page with proper canonical tag" bucket in Search
// Console. This component lets each page declare its own title, description,
// and a SELF-referencing canonical, so when the Netlify Prerender extension
// renders the page those correct tags are baked into the served HTML.
//
// Drop <Seo .../> at the top of each page component's returned JSX. Because
// react-helmet-async renders into <head>, placement in the body tree doesn't
// matter — put it wherever is convenient.
// ─────────────────────────────────────────────────────────────────────────

const SITE = 'https://windowwashingguide.com';
const DEFAULT_OG = `${SITE}/og-default.png`;

// Build an absolute canonical from a path. Always strips a trailing slash
// (except the root) so /states/ and /states canonicalize to one form, and
// guarantees a leading slash.
function canonicalFor(path) {
  if (!path) return SITE + '/';
  let p = path.trim();
  if (!p.startsWith('/')) p = '/' + p;
  if (p.length > 1 && p.endsWith('/')) p = p.replace(/\/+$/, '');
  return SITE + p;
}

// Clamp a description to a sane length for SERP snippets. Google truncates
// around 155–160 chars; we trim on a word boundary just under that.
function clampDescription(text) {
  if (!text) return '';
  const clean = String(text).replace(/\s+/g, ' ').trim();
  if (clean.length <= 160) return clean;
  const cut = clean.slice(0, 157);
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > 80 ? cut.slice(0, lastSpace) : cut) + '…';
}

export default function Seo({
  title,
  description,
  // The page's own path, e.g. "/articles/how-to-wash-a-window-properly".
  // Pass it explicitly so the canonical is correct even before hydration.
  path,
  // Optional overrides
  ogTitle,
  ogDescription,
  ogImage,
  ogType = 'article',
  // Set true ONLY on thin/utility routes you don't want indexed
  // (e.g. a bare thank-you page). Defaults to indexable.
  noindex = false,
  // Optional ISO date strings for article metadata
  publishedTime,
  modifiedTime,
  // Optional page-level JSON-LD. Accepts ONE of: an object, an already-
  // stringified payload (what utils/schema.js helpers return), or an array
  // of either. Each item becomes its own <script type="application/ld+json">.
  jsonLd,
  // s80 (audit D6) — a preset that builds the page's own schema from the
  // SAME title/description/path used for the tags above, so the schema can
  // never disagree with the head it sits in:
  //   'webApplication' → WebApplication + BreadcrumbList   (tool pages)
  //   'collectionPage' → CollectionPage  + BreadcrumbList   (hubs / indexes)
  //   'webPage'        → WebPage         + BreadcrumbList   (static pages)
  // Pages with richer schema (Article, BlogPosting, Person…) pass jsonLd
  // instead; the two compose — preset first, then jsonLd items.
  schemaType,
  // Breadcrumb trail for the preset, [{ name, path }] root→current. When
  // omitted the preset emits the two-item trail Guide → <title>.
  breadcrumbs,
}) {
  // Branded only when the branded form fits in 60 chars, and never doubled
  // when the title already carries the brand. See utils/pageTitle.js — that
  // module is the only place this rule is allowed to live.
  const fullTitle = buildPageTitle(title);
  const desc = clampDescription(description);
  const canonical = canonicalFor(path);
  const image = ogImage || DEFAULT_OG;

  // ── page-level JSON-LD ────────────────────────────────────────────────
  const ldBlocks = [];
  if (schemaType && path) {
    const args = { name: title || SITE_NAME, description: desc, path: canonical.slice(SITE.length) || '/' };
    const builder = {
      webApplication: webApplicationSchema,
      collectionPage: collectionPageSchema,
      webPage: webPageSchema,
    }[schemaType];
    if (!builder) throw new Error(`<Seo> unknown schemaType "${schemaType}"`);
    ldBlocks.push(builder(args));
    const trail = Array.isArray(breadcrumbs) && breadcrumbs.length
      ? breadcrumbs
      : [{ name: 'Guide', path: '/' }, { name: args.name, path: args.path }];
    ldBlocks.push(breadcrumbSchema(trail));
  }
  const extra = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  for (const item of extra) {
    if (!item) continue;
    ldBlocks.push(typeof item === 'string' ? item : JSON.stringify(item));
  }
  // Defensive: every block must parse. A malformed block would be emitted
  // on the live page silently; failing the render here is the loud version.
  for (const b of ldBlocks) JSON.parse(b);

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {desc && <meta name="description" content={desc} />}
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={
          noindex
            ? 'noindex, follow'
            : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
        }
      />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={ogTitle || title || SITE_NAME} />
      {(ogDescription || desc) && (
        <meta property="og:description" content={ogDescription || desc} />
      )}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle || title || SITE_NAME} />
      {(ogDescription || desc) && (
        <meta name="twitter:description" content={ogDescription || desc} />
      )}
      <meta name="twitter:image" content={image} />

      {/* Article timestamps, when supplied */}
      {publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}

      {/* Page-level structured data — one block per type (s80, audit D6) */}
      {ldBlocks.map((b, i) => (
        <script key={i} type="application/ld+json">{b}</script>
      ))}
    </Helmet>
  );
}
